import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { ASSET_CONDITIONS, ASSET_STATUSES, COMMERCIAL_STATUSES, FINANCIAL_FIELDS } from './assetModel';

const inputClass = 'bg-[#12141a] border-gray-700 text-white';
const selectContentClass = 'bg-[#1a1d23] border-gray-700';
const getValue = (form, path) => path.split('.').reduce((node, key) => node?.[key], form) ?? '';

function TextField({ label, path, form, onChange, type = 'text', placeholder, autoFocus }) {
  return (
    <div>
      <Label className="text-gray-300">{label}</Label>
      <Input
        type={type}
        value={getValue(form, path)}
        onChange={(event) => onChange(path, event.target.value)}
        className={inputClass}
        placeholder={placeholder}
        autoFocus={autoFocus}
      />
    </div>
  );
}

function SelectField({ label, value, onValueChange, options, placeholder, disabled }) {
  return (
    <div>
      <Label className="text-gray-300">{label}</Label>
      <Select value={value || undefined} onValueChange={onValueChange} disabled={disabled}>
        <SelectTrigger className={inputClass}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className={selectContentClass}>
          {options.map((option) => (
            <SelectItem key={option.code} value={option.code}>{option.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function MoreDetails({ title, children }) {
  return (
    <details className="group rounded-lg border border-gray-800">
      <summary className="cursor-pointer select-none px-4 py-3 text-sm text-gray-300 hover:text-white">
        {title}
      </summary>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-gray-800 p-4">{children}</div>
    </details>
  );
}

// Quick entry: the essentials are visible, everything else is folded away.
export function AssetFormDialog({
  open, onOpenChange, isEditing, form, onChange, onSubmit, submitting,
  clients, locations, categories,
}) {
  const clientOptions = clients.map((client) => ({ code: client._id, name: client.name || client.company_name || client.company }));
  const locationOptions = locations.map((location) => ({ code: location._id, name: location.name }));
  const commercialOptions = COMMERCIAL_STATUSES.map((code) => ({ code, name: code.replace(/_/g, ' ') }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1a1d23] border-gray-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#E2B05E]">{isEditing ? 'Editar Activo IT' : 'Nuevo Activo IT'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <SelectField label="Cliente *" value={form.client_id} onValueChange={(v) => onChange('client_id', v)} options={clientOptions} placeholder="Seleccionar cliente" />
            <SelectField
              label="Sucursal"
              value={form.location_id}
              onValueChange={(v) => onChange('location_id', v)}
              options={locationOptions}
              placeholder={!form.client_id ? 'Elige un cliente primero' : (locationOptions.length ? 'Opcional' : 'Sin sucursales')}
              disabled={!form.client_id || locationOptions.length === 0}
            />
            <SelectField label="Categoría *" value={form.category} onValueChange={(v) => onChange('category', v)} options={categories} />
            <TextField label="Nombre" path="name" form={form} onChange={onChange} placeholder="Ej: Laptop Recepción (opcional)" autoFocus />
            <TextField label="Marca" path="brand" form={form} onChange={onChange} />
            <TextField label="Modelo" path="model" form={form} onChange={onChange} />
            <TextField label="Serial" path="serial_number" form={form} onChange={onChange} />
            <TextField label="Asset Tag" path="tag" form={form} onChange={onChange} />
            <SelectField label="Estado" value={form.status} onValueChange={(v) => onChange('status', v)} options={ASSET_STATUSES} />
            <TextField label="Usuario asignado" path="assigned_to" form={form} onChange={onChange} />
          </div>

          <MoreDetails title="Más detalles (opcional): equipo, ubicación, garantía">
            <TextField label="Hostname" path="specs.hostname" form={form} onChange={onChange} />
            <TextField label="Sistema operativo" path="specs.operating_system" form={form} onChange={onChange} />
            <TextField label="Procesador" path="specs.processor" form={form} onChange={onChange} />
            <TextField label="RAM" path="specs.ram" form={form} onChange={onChange} placeholder="Ej: 16GB" />
            <TextField label="Almacenamiento" path="specs.storage" form={form} onChange={onChange} placeholder="Ej: 512GB SSD" />
            <TextField label="Dirección IP" path="specs.ip" form={form} onChange={onChange} />
            <TextField label="Dirección MAC" path="specs.mac" form={form} onChange={onChange} />
            <SelectField label="Condición" value={form.condition} onValueChange={(v) => onChange('condition', v)} options={ASSET_CONDITIONS} />
            <TextField label="Área" path="specs.area" form={form} onChange={onChange} />
            <TextField label="Ubicación detallada" path="specs.detailed_location" form={form} onChange={onChange} placeholder="Ej: Piso 2, Oficina 203" />
            <TextField label="Responsable del cliente" path="specs.client_contact" form={form} onChange={onChange} />
            <TextField label="Proveedor" path="specs.provider" form={form} onChange={onChange} />
            <TextField label="Fecha de compra" path="purchase_date" type="date" form={form} onChange={onChange} />
            <TextField label="Garantía hasta" path="warranty_expiry" type="date" form={form} onChange={onChange} />
            <TextField label="Código interno" path="specs.internal_code" form={form} onChange={onChange} />
            <SelectField label="Estado comercial" value={form.specs.commercial_status} onValueChange={(v) => onChange('specs.commercial_status', v)} options={commercialOptions} />
            <div className="sm:col-span-2">
              <Label className="text-gray-300">Notas</Label>
              <Textarea value={form.notes} onChange={(event) => onChange('notes', event.target.value)} className={inputClass} rows={2} />
            </div>
          </MoreDetails>

          <MoreDetails title="Costos y precio (opcional)">
            {FINANCIAL_FIELDS.map((field) => (
              <div key={field.key}>
                <Label className="text-gray-300">{field.label}</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.specs.financials?.[field.key] ?? 0}
                  onChange={(event) => onChange(`specs.financials.${field.key}`, parseFloat(event.target.value) || 0)}
                  className={inputClass}
                />
              </div>
            ))}
          </MoreDetails>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-gray-600 text-gray-300">
              Cancelar
            </Button>
            <Button type="submit" disabled={submitting} className="bg-[#E2B05E] hover:bg-[#c9993f] text-black">
              {submitting ? 'Guardando…' : `${isEditing ? 'Actualizar' : 'Crear'} Activo`}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
